import Controller, { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { dropTask } from 'ember-concurrency';
import type CompanyConfigurationModel from 'garaje/models/company-configuration';
import type SkinnyLocationModel from 'garaje/models/skinny-location';
import {
  AFTER_HOURS_ABSENCE_MINUTES_DEFAULT,
  AFTER_HOURS_ABSENCE_MINUTES_MAX,
  AFTER_HOURS_ABSENCE_MINUTES_MIN,
  buildBusinessHoursDays,
  canonicalizeBusinessHours,
  type BusinessHoursDayDraft,
  type BusinessHoursPayload,
  type DayWindowPayload,
} from 'garaje/pods/location-overview/manage/account-settings/utils/business-hours';
import {
  GEOFENCE_RADIUS_DEFAULT,
  GEOFENCE_RADIUS_MAX,
  GEOFENCE_RADIUS_MIN,
} from 'garaje/pods/location-overview/manage/account-settings/utils/geofence-radius-bounds';
import type {
  PresenceSettingsResponse,
  SettingsPayload,
  SignalTypeAbsencePayload,
  SignoutPayload,
} from 'garaje/pods/location-overview/manage/account-settings/utils/presence-settings-types';
import type ProtectedController from 'garaje/pods/protected/controller';
import type AjaxService from 'garaje/services/ajax';
import type AuthzService from 'garaje/services/authz';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type InactivityService from 'garaje/services/inactivity';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';
import type StateService from 'garaje/services/state';
import type StatsigService from 'garaje/services/statsig';
import { Permission } from 'garaje/utils/ui-permissions';
import urlBuilder from 'garaje/utils/url-builder';
import { cached, localCopy } from 'tracked-toolbox';

interface LocationOption {
  location: SkinnyLocationModel;
  disabled: boolean;
}

enum INACTIVITY_TIME_OPTIONS {
  FIFTEEN_MINS = '15 minutes',
  THIRTY_MINS = '30 minutes',
  ONE_HR = '1 hour',
  TWO_HRS = '2 hours',
}

type MillisecondsToTimeType = {
  [key: number]: INACTIVITY_TIME_OPTIONS;
};

const MILLISECONDS_TO_TIME_MAP: MillisecondsToTimeType = {
  900000: INACTIVITY_TIME_OPTIONS.FIFTEEN_MINS,
  1800000: INACTIVITY_TIME_OPTIONS.THIRTY_MINS,
  3600000: INACTIVITY_TIME_OPTIONS.ONE_HR,
  7200000: INACTIVITY_TIME_OPTIONS.TWO_HRS,
};

class LocationOverviewManageAccountSettingsController extends Controller {
  @service declare ajax: AjaxService;
  @service declare abilities: AbilitiesService;
  @service declare authz: AuthzService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare inactivity: InactivityService;
  @service declare skinnyLocations: SkinnyLocationsService;
  @service declare state: StateService;
  @service declare statsig: StatsigService;
  @service declare store: Store;

  @controller('protected') declare protectedController: ProtectedController;

  @tracked isInactivityBoxOpen: boolean = false;
  @localCopy('model.sessionInactivityTimeout', null) declare inactivityTimeout: number | null;

  @tracked isPresenceControlCenterOpen: boolean = false;
  @tracked selectedLocationId: string | null = null;
  @tracked macMappingsSearch = '';
  @tracked macMappingsSource = '';

  @tracked badgeEnabled: boolean = false;
  @tracked wifiEnabled: boolean = false;
  @tracked ssoEnabled: boolean = false;
  @tracked geoLocationEnabled: boolean = false;
  @tracked manualEnabled: boolean = false;
  @tracked mdmEnabled: boolean = false;
  @tracked isLoading: boolean = false;
  @tracked error: string | null = null;
  @tracked selectedPresenceTab: 'sign-in' | 'sign-out' | 'location-settings' = 'sign-in';

  // Aggregated auto sign-out configuration (any signal resets a single timer)
  @tracked absenceOfSignalsEnabled: boolean = false;
  @tracked absenceOfSignalsDuration: number = 3600000; // Default: 1 hour

  // Per-signal-type auto sign-out configuration (in UI order: badge, wifi, sso, mdm, geo-location)
  @tracked badgeAutoSignOutEnabled: boolean = false;
  @tracked badgeAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked wifiAutoSignOutEnabled: boolean = false;
  @tracked wifiAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked ssoAutoSignOutEnabled: boolean = false;
  @tracked ssoAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked mdmAutoSignOutEnabled: boolean = false;
  @tracked mdmAutoSignOutDuration: number = 3600000; // Default: 1 hour

  @tracked geoLocationAutoSignOutEnabled: boolean = false;

  // After-hours auto sign-out (EIP-2017): opt-in + interval (minutes) applied to signals outside the
  // business-hours window. Only meaningful once business hours is configured (the toggle is disabled
  // otherwise — see `businessHoursConfigured`). Default off / 60 min, matching the server default.
  @tracked afterHoursAutoSignOutEnabled: boolean = false;
  @tracked afterHoursAbsenceMinutes: number = AFTER_HOURS_ABSENCE_MINUTES_DEFAULT;
  @tracked afterHoursAbsenceMinutesError: string | null = null;

  // Per-day business hours (EIP-2017). An optional, location-level overlay on whichever auto
  // sign-out interval is configured: during a weekday's open window that interval applies; outside
  // it (or on a closed day) the fire time is capped short.
  //
  // All three tabs use an explicit draft → Save flow: the tracked fields above are the editable
  // drafts, and nothing is persisted until that tab's Save runs. The `persisted*` snapshots below
  // hold each tab's last saved/loaded value (API shape). On any tab's Save we send the full document
  // built from that tab's draft plus the *other* tabs' persisted snapshots — so unsaved edits on a
  // different tab are never promoted and never leak into the PUT.
  @tracked businessHoursEnabled: boolean = false;
  // After-hours presence report opt-in (lives in the business-hours block). Defaults off; admins who
  // opt in receive a report of people still on-site after the location's business hours close. Only
  // meaningful when business hours is on, so the row is hidden — and the flag forced false on
  // save — whenever businessHoursEnabled is off (mirrors how the weekday rows are gated).
  @tracked afterHoursReportEnabled: boolean = false;
  @tracked businessHoursDays: BusinessHoursDayDraft[] = buildBusinessHoursDays();
  @tracked persistedBusinessHours: BusinessHoursPayload | null = null;
  @tracked persistedSignIn: SettingsPayload | null = null;
  @tracked persistedSignOut: SignoutPayload | null = null;

  // Location-level "Automatic sign-out" (time-of-day) fallback — lives on the full Location
  // model as `autoSignOutAtMinutesSinceMidnight` (enabled ⇔ non-null, matching
  // components/location-settings/sign-out/auto-sign-out-time.gts). Used only to surface a
  // fallback-explanation tooltip on the PCC Sign-Out tab.
  @tracked locationAutoSignOutEnabled: boolean = false;

  // Per-location geofence radius (meters) — Unit 4 of EIP-3334.
  // Display default of 150 m matches the existing mobile fallback so behavior is unchanged
  // for locations whose server-side column is still NULL. Bounds mirror the server CHECK
  // constraint exactly (see utils/geofence-radius-bounds.ts).
  @tracked geoLocationRadius: number = GEOFENCE_RADIUS_DEFAULT;
  @tracked geoLocationRadiusError: string | null = null;

  declare model: CompanyConfigurationModel | undefined;

  get headerTitle(): string {
    const companyName = this.state.currentCompany?.name;
    return companyName ? `Account settings for ${companyName}` : 'Account settings';
  }

  get inactivityDescription(): INACTIVITY_TIME_OPTIONS | undefined {
    const sessionTimeout: number = this.model?.sessionInactivityTimeout || 7200000;
    return MILLISECONDS_TO_TIME_MAP[sessionTimeout];
  }

  get isInactivityEnabled(): boolean {
    const companyConfiguration = this.model;
    return Boolean(companyConfiguration?.sessionInactivityTimeout);
  }

  get inactivityOptions(): Array<number> {
    return [900000, 1800000, 3600000, 7200000];
  }

  get isSaveBtnDisabled(): boolean {
    return this.model?.sessionInactivityTimeout === this.inactivityTimeout;
  }

  @cached
  get availableLocations(): LocationOption[] {
    const locations = this.skinnyLocations.currentCompanyLocations || [];

    // Map locations to create copy of location with 'disabled' property updated
    // PowerSelect checks 'disabled' by default and grays out options
    // disabled_to_employees_at (not 'disabled') controls employee visibility
    return locations.map((location) => ({
      location,
      disabled: !!location.disabledToEmployeesAt,
    }));
  }

  get selectedLocation(): LocationOption | null {
    if (!this.selectedLocationId) return null;
    const options = this.availableLocations;

    return options.find((option) => option.location.id === this.selectedLocationId) || null;
  }

  get shouldShowToggles(): boolean {
    return !!this.selectedLocationId;
  }

  get isInactivityTimeoutEnabled(): boolean {
    return !!this.state.features?.canAccessInactivityLogout;
  }

  get isPresenceControlCenterEnabled(): boolean {
    return this.authz.hasPermissionAtCurrentCompany(Permission.ACCOUNT_MANAGEMENT_PRODUCT_SETTINGS_READ);
  }

  get isGeofenceRadiusEnabled(): boolean {
    return this.featureFlags.isEnabled('presence-control-center-geofence-radius');
  }

  // Gates the business-hours UI only (Location settings tab + the sign-out tab's Business hours
  // section). Save logic stays unconditional, so a draft saved before the flag flips off still persists.
  get isBusinessHoursEnabled(): boolean {
    return this.featureFlags.isEnabled('presence-control-center-business-hours');
  }

  // Bounds + default re-exposed to the template — keeps the values in a single source
  // (utils/geofence-radius-bounds.ts) and out of the .hbs file.
  get geofenceRadiusMin(): number {
    return GEOFENCE_RADIUS_MIN;
  }

  get geofenceRadiusMax(): number {
    return GEOFENCE_RADIUS_MAX;
  }

  get geofenceRadiusDefault(): number {
    return GEOFENCE_RADIUS_DEFAULT;
  }

  get showMacAddressMappings(): boolean {
    return this.abilities.can('view mac-address-mapping');
  }

  get companyId(): string {
    return this.state.currentCompany?.id ?? '';
  }

  get allControlsEnabled(): boolean {
    return (
      this.badgeEnabled &&
      this.wifiEnabled &&
      this.ssoEnabled &&
      this.geoLocationEnabled &&
      this.manualEnabled &&
      this.mdmEnabled
    );
  }

  get anySignOutSettingEnabled(): boolean {
    return (
      this.absenceOfSignalsEnabled ||
      this.badgeAutoSignOutEnabled ||
      this.wifiAutoSignOutEnabled ||
      this.ssoAutoSignOutEnabled ||
      this.mdmAutoSignOutEnabled ||
      this.geoLocationAutoSignOutEnabled
    );
  }

  // Notice explains that, when PCC will sign employees out on signal absence AND the
  // location also has time-based Automatic sign-out on, the location setting is a fallback.
  get showSignOutFallbackNotice(): boolean {
    return this.locationAutoSignOutEnabled && this.anySignOutSettingEnabled;
  }

  get absenceOfSignalsDurationOptions(): Array<{ value: number; label: string }> {
    return [
      { value: 3600000, label: '1 hour' },
      { value: 7200000, label: '2 hours' },
      { value: 14400000, label: '4 hours' },
      { value: 28800000, label: '8 hours' },
    ];
  }

  get selectedAbsenceOfSignalsDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.absenceOfSignalsDuration);
  }

  get selectedBadgeDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.badgeAutoSignOutDuration);
  }

  get selectedWifiDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.wifiAutoSignOutDuration);
  }

  get selectedSsoDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.ssoAutoSignOutDuration);
  }

  get selectedMdmDuration(): { value: number; label: string } | undefined {
    return this.absenceOfSignalsDurationOptions.find((option) => option.value === this.mdmAutoSignOutDuration);
  }

  // A saveable state: master off, or every enabled day has a complete, ordered window. No defaults
  // are pre-filled, so an untouched "on" day has null times and isn't valid yet.
  get businessHoursValid(): boolean {
    return !this.businessHoursEnabled || this.businessHoursDays.every((day) => day.valid);
  }

  // Business hours is "configured" once it's been saved with the master switch on — the only state
  // in which after-hours auto sign-out can take effect (entry-recorder no-ops it otherwise). Gated on
  // the *persisted* config (not the unsaved draft) because the after-hours toggle auto-saves and
  // should reflect what's actually in effect. Shared gate for the after-hours sign-out toggle here and
  // the future after-hours sign-in toggle.
  get businessHoursConfigured(): boolean {
    return this.persistedBusinessHours !== null && this.persistedBusinessHours.enabled;
  }

  // Whether the after-hours interval input holds a valid value (whole number within bounds). Blocks
  // the auto-save when false and drives the inline error.
  get afterHoursAbsenceMinutesValid(): boolean {
    return (
      Number.isInteger(this.afterHoursAbsenceMinutes) &&
      this.afterHoursAbsenceMinutes >= AFTER_HOURS_ABSENCE_MINUTES_MIN &&
      this.afterHoursAbsenceMinutes <= AFTER_HOURS_ABSENCE_MINUTES_MAX
    );
  }

  // The full per-day payload that would be persisted (null only when fully unconfigured: master off
  // and nothing stored). Only valid to read when `businessHoursValid`. `after-hours-absence-minutes`
  // is omitted — the server defaults it to 60.
  get businessHoursDraft(): BusinessHoursPayload | null {
    if (!this.businessHoursEnabled && this.persistedBusinessHours === null) return null;
    const days: Record<string, DayWindowPayload> = {};
    this.businessHoursDays.forEach((day) => {
      days[day.key] = day.apiWindow;
    });
    // Force the after-hours report opt-in off whenever business hours is disabled, so disabling
    // business hours clears the report flag in the *same* PUT (the report can't fire without a
    // configured window). When business hours is on we persist the admin's choice.
    return {
      enabled: this.businessHoursEnabled,
      'after-hours-report-enabled': this.businessHoursEnabled && this.afterHoursReportEnabled,
      days,
    };
  }

  // The draft differs from what's persisted. An invalid (incomplete/out-of-order) day counts as
  // dirty so the user isn't stuck — but canSaveBusinessHours still blocks the actual save below.
  get businessHoursDirty(): boolean {
    if (!this.businessHoursValid) return true;
    const draft = this.businessHoursDraft;
    const persisted = this.persistedBusinessHours;
    if (draft === null && persisted === null) return false;
    if (draft === null || persisted === null) return true;
    // Compare canonically (fixed weekday order, closed days collapsed) — see canonicalizeBusinessHours.
    return JSON.stringify(canonicalizeBusinessHours(draft)) !== JSON.stringify(canonicalizeBusinessHours(persisted));
  }

  // Save is offered only when there's a valid change to commit.
  get canSaveBusinessHours(): boolean {
    return this.businessHoursValid && this.businessHoursDirty;
  }

  // Convenience inverse for the Save button's `disabled` binding (matches the page's getter style).
  get businessHoursSaveDisabled(): boolean {
    return !this.canSaveBusinessHours;
  }

  // The Save button (and the per-day rows, via the master toggle) only appear once the admin is
  // configuring business hours or a config already exists. A fresh location with the toggle off shows
  // just the enable toggle. Persisted-but-toggled-off still shows Save so the admin can commit it.
  get showBusinessHoursSave(): boolean {
    return this.businessHoursEnabled || this.persistedBusinessHours !== null;
  }

  // ---- Sign-in / Sign-out draft → Save (mirrors the business-hours trio above) -----------------

  // The Sign-in `settings` object built from the toggle drafts. The geo-location radius is included
  // only when the feature flag is on (matching the PUT contract / persisted snapshot).
  get signInDraft(): SettingsPayload {
    const geoLocationEntry: SettingsPayload['geo-location'] = {
      enabled: this.geoLocationEnabled,
      schedule: null,
    };
    if (this.isGeofenceRadiusEnabled) {
      geoLocationEntry['geofence-radius-meters'] = this.geoLocationRadius;
    }
    return {
      badge: { enabled: this.badgeEnabled, schedule: null },
      wifi: { enabled: this.wifiEnabled, schedule: null },
      sso: { enabled: this.ssoEnabled, schedule: null },
      'geo-location': geoLocationEntry,
      manual: { enabled: this.manualEnabled, schedule: null },
      mdm: { enabled: this.mdmEnabled, schedule: null },
    };
  }

  // The Sign-out `signout-settings` object built from the aggregated / per-signal / after-hours drafts.
  get signOutDraft(): SignoutPayload {
    const signalTypeAbsence: SignalTypeAbsencePayload = {};
    if (this.badgeAutoSignOutEnabled) signalTypeAbsence.badge = Math.floor(this.badgeAutoSignOutDuration / 60000);
    if (this.wifiAutoSignOutEnabled) signalTypeAbsence.wifi = Math.floor(this.wifiAutoSignOutDuration / 60000);
    if (this.ssoAutoSignOutEnabled) signalTypeAbsence.sso = Math.floor(this.ssoAutoSignOutDuration / 60000);
    if (this.mdmAutoSignOutEnabled) signalTypeAbsence.mdm = Math.floor(this.mdmAutoSignOutDuration / 60000);
    if (this.geoLocationAutoSignOutEnabled) signalTypeAbsence['geo-location'] = 60; // unused by entry-recorder for active sign-out

    const anyPerSignalTypeEnabled =
      this.badgeAutoSignOutEnabled ||
      this.wifiAutoSignOutEnabled ||
      this.ssoAutoSignOutEnabled ||
      this.mdmAutoSignOutEnabled ||
      this.geoLocationAutoSignOutEnabled;
    const anyAutoSignOutEnabled = this.absenceOfSignalsEnabled || anyPerSignalTypeEnabled;

    // Force the after-hours opt-in off unless business hours is configured (the toggle is disabled
    // then), so we never persist an opt-in that can't take effect.
    const afterHoursEnabled = this.businessHoursConfigured && this.afterHoursAutoSignOutEnabled;

    return {
      enabled: anyAutoSignOutEnabled,
      'signal-absence-minutes': this.absenceOfSignalsEnabled ? Math.floor(this.absenceOfSignalsDuration / 60000) : null,
      'signal-type-absence': Object.keys(signalTypeAbsence).length > 0 ? signalTypeAbsence : null,
      'enable-after-hours-auto-sign-out': afterHoursEnabled,
      'after-hours-absence-minutes': afterHoursEnabled
        ? this.afterHoursAbsenceMinutes
        : AFTER_HOURS_ABSENCE_MINUTES_DEFAULT,
    };
  }

  // Dirty = draft differs from the last saved/loaded snapshot. Both sides come from the same draft
  // getter (the snapshot is captured from it), so the key order is stable for the JSON compare.
  get signInDirty(): boolean {
    return JSON.stringify(this.signInDraft) !== JSON.stringify(this.persistedSignIn);
  }

  get signOutDirty(): boolean {
    return JSON.stringify(this.signOutDraft) !== JSON.stringify(this.persistedSignOut);
  }

  // Sign-in's only validation is the geofence radius input; Sign-out's is the after-hours interval.
  // Each error only blocks the Save while the input that produces it is actually rendered — gated on
  // the same conditions as the template. Otherwise a stale error from a now-hidden input (e.g. typing
  // a bad radius, then toggling Geo Location off) would silently keep the Save disabled with nothing
  // on screen to fix.
  get signInValid(): boolean {
    return !(this.isGeofenceRadiusEnabled && this.geoLocationEnabled && this.geoLocationRadiusError);
  }

  get signOutValid(): boolean {
    return !(this.businessHoursConfigured && this.afterHoursAutoSignOutEnabled && this.afterHoursAbsenceMinutesError);
  }

  get canSaveSignIn(): boolean {
    return this.signInDirty && this.signInValid;
  }

  get signInSaveDisabled(): boolean {
    return !this.canSaveSignIn;
  }

  get canSaveSignOut(): boolean {
    return this.signOutDirty && this.signOutValid;
  }

  get signOutSaveDisabled(): boolean {
    return !this.canSaveSignOut;
  }

  // Seed the per-day drafts + persisted snapshot from the API's business-hours block (or reset to a
  // fresh/closed state when absent). buildBusinessHoursDays always returns all 7 day rows.
  private loadBusinessHours(businessHours?: PresenceSettingsResponse['data']['attributes']['business-hours']): void {
    this.businessHoursEnabled = businessHours?.enabled ?? false;
    // Default the report opt-in off when the field is absent/null for this location.
    this.afterHoursReportEnabled = businessHours?.['after-hours-report-enabled'] ?? false;
    this.businessHoursDays = buildBusinessHoursDays(businessHours);
    this.persistedBusinessHours = businessHours
      ? {
          enabled: businessHours.enabled,
          'after-hours-report-enabled': businessHours['after-hours-report-enabled'] ?? false,
          days: businessHours.days ?? {},
        }
      : null;
  }

  displayTimeoutPeriod(timeout: number): string {
    return MILLISECONDS_TO_TIME_MAP[timeout] || '';
  }

  @action
  openInactivityBox(): void {
    this.isInactivityBoxOpen = true;
  }

  @action
  closeInactivityBox(): void {
    this.isInactivityBoxOpen = false;
    this.inactivityTimeout = this.model?.sessionInactivityTimeout || null;
  }

  @action
  togglePresenceControlCenter(): void {
    this.isPresenceControlCenterOpen = !this.isPresenceControlCenterOpen;
  }

  @action
  selectPresenceTab(tabName: 'sign-in' | 'sign-out' | 'location-settings'): void {
    this.selectedPresenceTab = tabName;
  }

  @action
  async selectLocation(option: LocationOption): Promise<void> {
    this.selectedLocationId = option.location.id;
    await this.loadPresenceSettings(this.selectedLocationId);
    await this.loadLocationAutoSignOut(this.selectedLocationId);
  }

  // Deep-link to the selected location's "Automatic sign-out" settings. We can't rely on a
  // <LinkTo> with a `location` query param — the application route only reads that param on a
  // full page boot, not on in-app transitions — so we switch the current location and
  // transition the same way the locations table's "View settings" link does.
  @action
  viewLocationSettings(): void {
    if (!this.selectedLocationId) return;
    this.protectedController.send('switchLocation', this.selectedLocationId, 'manage.location-settings');
  }

  // Sign-in / Sign-out controls mutate draft state only; nothing is persisted until that tab's Save.

  @action
  toggleBadge(enabled: boolean): void {
    this.badgeEnabled = enabled;
  }

  @action
  toggleWifi(enabled: boolean): void {
    this.wifiEnabled = enabled;
  }

  @action
  toggleSSO(enabled: boolean): void {
    this.ssoEnabled = enabled;
  }

  @action
  toggleGeoLocation(enabled: boolean): void {
    this.geoLocationEnabled = enabled;
  }

  @action
  toggleManual(enabled: boolean): void {
    this.manualEnabled = enabled;
  }

  @action
  toggleMDM(enabled: boolean): void {
    this.mdmEnabled = enabled;
  }

  @action
  toggleAllControls(enabled: boolean): void {
    this.badgeEnabled = enabled;
    this.wifiEnabled = enabled;
    this.ssoEnabled = enabled;
    this.geoLocationEnabled = enabled;
    this.manualEnabled = enabled;
    this.mdmEnabled = enabled;
  }

  @action
  toggleAbsenceOfSignals(enabled: boolean): void {
    this.absenceOfSignalsEnabled = enabled;
  }

  @action
  selectAbsenceOfSignalsDuration(option: { value: number; label: string }): void {
    this.absenceOfSignalsDuration = option.value;
  }

  @action
  toggleBadgeAutoSignOut(enabled: boolean): void {
    this.badgeAutoSignOutEnabled = enabled;
  }

  @action
  selectBadgeDuration(option: { value: number; label: string }): void {
    this.badgeAutoSignOutDuration = option.value;
  }

  @action
  toggleWifiAutoSignOut(enabled: boolean): void {
    this.wifiAutoSignOutEnabled = enabled;
  }

  @action
  selectWifiDuration(option: { value: number; label: string }): void {
    this.wifiAutoSignOutDuration = option.value;
  }

  @action
  toggleSsoAutoSignOut(enabled: boolean): void {
    this.ssoAutoSignOutEnabled = enabled;
  }

  @action
  selectSsoDuration(option: { value: number; label: string }): void {
    this.ssoAutoSignOutDuration = option.value;
  }

  @action
  toggleMdmAutoSignOut(enabled: boolean): void {
    this.mdmAutoSignOutEnabled = enabled;
  }

  @action
  selectMdmDuration(option: { value: number; label: string }): void {
    this.mdmAutoSignOutDuration = option.value;
  }

  @action
  toggleGeoLocationAutoSignOut(enabled: boolean): void {
    this.geoLocationAutoSignOutEnabled = enabled;
  }

  @action
  toggleAfterHoursAutoSignOut(enabled: boolean): void {
    this.afterHoursAutoSignOutEnabled = enabled;
    // Clear a stale interval error when toggling off so it can't block the Sign-out save.
    if (!enabled) this.afterHoursAbsenceMinutesError = null;
  }

  // Validate the after-hours interval input (whole number, 1..1440). Sets the inline error and leaves
  // the tracked value untouched on bad input; the Sign-out Save is gated on the absence of this error.
  @action
  validateAfterHoursAbsenceMinutes(event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = target.value.trim();
    const errorMsg = `Must be a whole number between ${AFTER_HOURS_ABSENCE_MINUTES_MIN} and ${AFTER_HOURS_ABSENCE_MINUTES_MAX} minutes`;
    if (raw === '' || !/^\d+$/.test(raw)) {
      this.afterHoursAbsenceMinutesError = errorMsg;
      return;
    }
    const value = Number(raw);
    if (value < AFTER_HOURS_ABSENCE_MINUTES_MIN || value > AFTER_HOURS_ABSENCE_MINUTES_MAX) {
      this.afterHoursAbsenceMinutesError = errorMsg;
      return;
    }
    this.afterHoursAbsenceMinutes = value;
    this.afterHoursAbsenceMinutesError = null;
  }

  @action
  validateGeofenceRadius(event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = target.value.trim();
    const errorMsg = `Radius must be a whole number between ${GEOFENCE_RADIUS_MIN} and ${GEOFENCE_RADIUS_MAX} meters`;
    if (raw === '' || !/^-?\d+$/.test(raw)) {
      this.geoLocationRadiusError = errorMsg;
      return;
    }
    const value = Number(raw);
    if (value < GEOFENCE_RADIUS_MIN || value > GEOFENCE_RADIUS_MAX) {
      this.geoLocationRadiusError = errorMsg;
      return;
    }
    this.geoLocationRadius = value;
    this.geoLocationRadiusError = null;
  }

  // Persist just the Sign-in tab: sends the Sign-in draft plus the other tabs' persisted snapshots.
  @action
  async saveSignIn(): Promise<void> {
    if (!this.canSaveSignIn) return;
    await this.savePresenceSettings('sign-in');
  }

  // Persist just the Sign-out tab: sends the Sign-out draft plus the other tabs' persisted snapshots.
  @action
  async saveSignOut(): Promise<void> {
    if (!this.canSaveSignOut) return;
    await this.savePresenceSettings('sign-out');
  }

  // Business hours toggles and time pickers only mutate draft state — nothing is persisted until
  // saveBusinessHours.
  @action
  toggleBusinessHours(enabled: boolean): void {
    this.businessHoursEnabled = enabled;
    // Turning business hours off also drops the after-hours report opt-in (kept consistent with the
    // forced-false on save, so the row reflects the value that will actually be persisted).
    if (!enabled) this.afterHoursReportEnabled = false;
  }

  @action
  toggleAfterHoursReport(enabled: boolean): void {
    this.afterHoursReportEnabled = enabled;
  }

  @action
  toggleBusinessHoursDay(day: BusinessHoursDayDraft, enabled: boolean): void {
    day.enabled = enabled;
  }

  @action
  setBusinessHoursDayStart(day: BusinessHoursDayDraft, time: string): void {
    day.start = time;
  }

  @action
  setBusinessHoursDayEnd(day: BusinessHoursDayDraft, time: string): void {
    day.end = time;
  }

  @action
  async saveBusinessHours(): Promise<void> {
    if (!this.selectedLocationId) return;
    // Validate on submit: every enabled day must have both times set and start before end.
    if (!this.businessHoursValid) {
      const offending = this.businessHoursDays.find((day) => day.enabled && !day.rangeValid);
      const message =
        offending?.outOfOrder === true
          ? `${offending.label}: start time must be before the end time`
          : 'Set a start and end time for each open day';
      this.flashMessages.showAndHideFlash('error', message);
      return;
    }
    // Send the business-hours draft; savePresenceSettings promotes it to the persisted snapshot on
    // success and flashes on success/failure.
    await this.savePresenceSettings('location');
  }

  enableInactivityTask = dropTask(async () => {
    const companyConfiguration = this.model;
    try {
      if (companyConfiguration) {
        this.inactivityTimeout = 7200000;
        companyConfiguration.sessionInactivityTimeout = this.inactivityTimeout;
        await companyConfiguration.save();

        // start the timer
        await this.inactivity.initialize();
        this.flashMessages.showAndHideFlash('success', 'Inactivity based timeout enabled');
        this.openInactivityBox();
      }
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Something went wrong, please try again');
    }
  });

  disableInactivityTask = dropTask(async () => {
    const companyConfiguration = this.model;
    try {
      if (companyConfiguration) {
        this.inactivityTimeout = null;
        companyConfiguration.sessionInactivityTimeout = this.inactivityTimeout;
        await companyConfiguration.save();

        // stop the timer
        this.inactivity.cleanUp();
        this.flashMessages.showAndHideFlash('success', 'Inactivity based timeout disabled');
        this.isInactivityBoxOpen = false;
      }
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Something went wrong, please try again');
    }
  });

  saveInactivityTask = dropTask(async () => {
    const companyConfiguration = this.model;
    try {
      if (companyConfiguration) {
        companyConfiguration.sessionInactivityTimeout = this.inactivityTimeout;
        await companyConfiguration.save();
        // change the timeout duration
        this.inactivity.timeoutDuration = this.inactivityTimeout;
        this.flashMessages.showAndHideFlash('success', 'Inactivity timeout period updated');
        this.isInactivityBoxOpen = false;
      }
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Something went wrong, please try again');
    }
  });

  async loadPresenceSettings(locationId: string): Promise<void> {
    if (!locationId) return;

    try {
      this.isLoading = true;
      this.error = null;

      const url = urlBuilder.presenceSettingsUrl(locationId);

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      const response = (await this.ajax.request(url, {
        method: 'GET',
      })) as PresenceSettingsResponse;

      // Map API response to UI state
      const settings = response.data?.attributes?.settings || {};

      this.badgeEnabled = settings.badge?.enabled ?? true;
      this.wifiEnabled = settings.wifi?.enabled ?? true;
      this.ssoEnabled = settings.sso?.enabled ?? true;
      this.geoLocationEnabled = settings['geo-location']?.enabled ?? true;
      this.manualEnabled = settings.manual?.enabled ?? true;
      this.mdmEnabled = settings.mdm?.enabled ?? true;

      // Geofence radius — only overwrite the tracked default when the API surfaces a real
      // value. `null` (column unset on the server) means "use the display default 150".
      const apiRadius = settings['geo-location']?.['geofence-radius-meters'];
      if (apiRadius != null) {
        this.geoLocationRadius = apiRadius;
      } else {
        this.geoLocationRadius = GEOFENCE_RADIUS_DEFAULT;
      }
      this.geoLocationRadiusError = null;

      // Load sign-out settings
      const signoutSettings = response.data?.attributes?.['signout-settings'];
      const signalTypeAbsence = signoutSettings?.['signal-type-absence'];
      const signalAbsenceMinutes = signoutSettings?.['signal-absence-minutes'];

      // Load per-day business hours (top-level, location-level — not under signout-settings).
      // Seed both the per-day drafts and the persisted snapshot so the section starts clean
      // (not dirty) and auto-saves from other controls preserve the stored config.
      this.loadBusinessHours(response.data?.attributes?.['business-hours']);

      // Load after-hours auto sign-out (EIP-2017). Opt-in + interval, defaulting off / 60.
      this.afterHoursAutoSignOutEnabled = signoutSettings?.['enable-after-hours-auto-sign-out'] ?? false;
      this.afterHoursAbsenceMinutes =
        signoutSettings?.['after-hours-absence-minutes'] ?? AFTER_HOURS_ABSENCE_MINUTES_DEFAULT;

      // Load aggregated settings (independent of per-signal-type)
      if (signalAbsenceMinutes != null) {
        this.absenceOfSignalsEnabled = signoutSettings?.enabled ?? false;
        this.absenceOfSignalsDuration = signalAbsenceMinutes * 60000;
      } else {
        this.absenceOfSignalsEnabled = false;
        this.absenceOfSignalsDuration = 3600000;
      }

      // Load per-signal-type settings (independent of aggregated)
      if (signalTypeAbsence) {
        this.badgeAutoSignOutEnabled = signalTypeAbsence.badge != null;
        this.badgeAutoSignOutDuration = signalTypeAbsence.badge ? signalTypeAbsence.badge * 60000 : 3600000;

        this.wifiAutoSignOutEnabled = signalTypeAbsence.wifi != null;
        this.wifiAutoSignOutDuration = signalTypeAbsence.wifi ? signalTypeAbsence.wifi * 60000 : 3600000;

        this.ssoAutoSignOutEnabled = signalTypeAbsence.sso != null;
        this.ssoAutoSignOutDuration = signalTypeAbsence.sso ? signalTypeAbsence.sso * 60000 : 3600000;

        this.mdmAutoSignOutEnabled = signalTypeAbsence.mdm != null;
        this.mdmAutoSignOutDuration = signalTypeAbsence.mdm ? signalTypeAbsence.mdm * 60000 : 3600000;

        this.geoLocationAutoSignOutEnabled = signalTypeAbsence['geo-location'] != null;
      } else {
        // Default to OFF - user must opt-in to per-signal-type
        this.badgeAutoSignOutEnabled = false;
        this.badgeAutoSignOutDuration = 3600000;

        this.wifiAutoSignOutEnabled = false;
        this.wifiAutoSignOutDuration = 3600000;

        this.ssoAutoSignOutEnabled = false;
        this.ssoAutoSignOutDuration = 3600000;

        this.mdmAutoSignOutEnabled = false;
        this.mdmAutoSignOutDuration = 3600000;

        this.geoLocationAutoSignOutEnabled = false;
      }

      // Capture the loaded state as each tab's persisted snapshot so all three tabs start not-dirty.
      // (loadBusinessHours already set persistedBusinessHours.) Built from the same draft getters the
      // dirty-diff and Save use, so they compare equal until the admin edits something.
      this.persistedSignIn = this.signInDraft;
      this.persistedSignOut = this.signOutDraft;
    } catch (_error) {
      this.error = 'Failed to load presence settings';
      this.flashMessages.showAndHideFlash('error', 'Failed to load presence settings');
      // Reset to defaults on error
      this.badgeEnabled = false;
      this.wifiEnabled = false;
      this.ssoEnabled = false;
      this.geoLocationEnabled = false;
      this.manualEnabled = false;
      this.mdmEnabled = false;

      this.badgeAutoSignOutEnabled = false;
      this.badgeAutoSignOutDuration = 3600000;
      this.wifiAutoSignOutEnabled = false;
      this.wifiAutoSignOutDuration = 3600000;
      this.ssoAutoSignOutEnabled = false;
      this.ssoAutoSignOutDuration = 3600000;
      this.mdmAutoSignOutEnabled = false;
      this.mdmAutoSignOutDuration = 3600000;
      this.geoLocationAutoSignOutEnabled = false;

      this.absenceOfSignalsEnabled = false;
      this.absenceOfSignalsDuration = 3600000;

      this.afterHoursAutoSignOutEnabled = false;
      this.afterHoursAbsenceMinutes = AFTER_HOURS_ABSENCE_MINUTES_DEFAULT;

      this.loadBusinessHours(undefined);

      this.geoLocationRadius = GEOFENCE_RADIUS_DEFAULT;
      this.geoLocationRadiusError = null;

      // Keep the snapshots in sync with the reset draft state so the tabs aren't spuriously dirty.
      this.persistedSignIn = this.signInDraft;
      this.persistedSignOut = this.signOutDraft;
    } finally {
      this.isLoading = false;
    }
  }

  async loadLocationAutoSignOut(locationId: string): Promise<void> {
    if (!locationId) {
      this.locationAutoSignOutEnabled = false;
      return;
    }
    try {
      // `reload: true` — the application adapter never background-reloads records
      // (adapters/application.js#shouldBackgroundReloadRecord), so a cached Location could
      // carry a stale `autoSignOutAtMinutesSinceMidnight`. Force a fresh fetch so the
      // tooltip reflects the location's current setting. Matches the convention used by
      // other location loads that need fresh data (e.g. visitors/settings/location/route).
      const location = await this.store.findRecord('location', locationId, { reload: true });
      this.locationAutoSignOutEnabled = location.autoSignOutAtMinutesSinceMidnight !== null;
    } catch (_error) {
      // The tooltip is purely informational — never block the page on this.
      this.locationAutoSignOutEnabled = false;
    }
  }

  // PUTs the full presence-settings document for one tab's Save. The active `section` contributes its
  // draft; the other two sections are sent from their *persisted* snapshots, so unsaved edits on a
  // different tab are never persisted. The endpoint requires `settings` and treats an omitted
  // business-hours as a delete, so we always send all three (the `?? draft` fallbacks only matter
  // before the first load, when no snapshot exists yet). On success we promote the saved section's
  // snapshot so the tab goes clean and subsequent saves of other tabs carry the new value.
  async savePresenceSettings(section: 'sign-in' | 'sign-out' | 'location'): Promise<void> {
    if (!this.selectedLocationId) return;

    try {
      this.error = null;

      // Pulled out of the ternaries below so the `??` fallback isn't nested inside `?:` — Prettier
      // 3.1 (pre-commit hook) and 3.3 (CI) format that nesting differently, which fights on every commit.
      const signInFallback = this.persistedSignIn ?? this.signInDraft;
      const signOutFallback = this.persistedSignOut ?? this.signOutDraft;
      const settings = section === 'sign-in' ? this.signInDraft : signInFallback;
      const signoutSettings = section === 'sign-out' ? this.signOutDraft : signOutFallback;
      const businessHours = section === 'location' ? this.businessHoursDraft : this.persistedBusinessHours;

      // Stamp the location's IANA timezone at save time so the local HH:mm windows are self-contained
      // for downstream consumers (derived from the location, kept out of the draft/dirty-diff). A null
      // business-hours payload clears the stored config (matches the endpoint's PUT-replace semantics).
      const businessHoursPayload = businessHours
        ? { ...businessHours, timezone: this.selectedLocation?.location.timezone }
        : null;

      const url = urlBuilder.presenceSettingsUrl(this.selectedLocationId);
      await this.ajax.request(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          data: {
            type: 'presence-settings',
            attributes: {
              settings,
              'signout-settings': signoutSettings,
              // Top-level: business hours is independent of sign-out settings.
              'business-hours': businessHoursPayload,
            },
          },
        },
      });

      // Promote only the saved section's snapshot to its draft.
      if (section === 'sign-in') {
        this.persistedSignIn = this.signInDraft;
      } else if (section === 'sign-out') {
        this.persistedSignOut = this.signOutDraft;
      } else {
        this.persistedBusinessHours = this.businessHoursDraft;
      }

      this.flashMessages.showAndHideFlash('success', 'Presence settings saved');
    } catch (_error) {
      this.error = 'Failed to save presence settings';
      this.flashMessages.showAndHideFlash('error', 'Failed to save presence settings');
    }
  }
}

export default LocationOverviewManageAccountSettingsController;
